import { initContract } from '@ts-rest/core'
import { ErrorResponseSchema } from './general'
import { Order, Subscription } from '@repo/api-types'
import {
  FeedingPlanServingSize,
  FeedingPlanType,
  GeneratedPlan,
  GeneratePlansBodySchema,
  UpdateDogProfileInputSchema,
  UpdateDogProfileResult,
  UpdateSubscriptionBodyExtraSchema,
  UpdateSubscriptionBodyFeedingPlanSkuSchema,
  ReviewUpdateSubscriptionPayloadSchema,
  CancelReasonContext,
  CancelReasonSchema,
  ExtraType,
  DeliverySlotWindow,
  SubscriptionSchema,
} from '@lyka-pet-food/contracts'
import { z } from 'zod'

const c = initContract()

export const subscriptionContract = c.router(
  {
    cancelSubscription: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/cancel',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        primaryCancelReasonId: z.string().optional(),
        secondaryCancelReasonId: z.string().optional(),
        otherCancelReason: z.string().optional(),
      }),
      responses: {
        200: z.boolean(),
      },
    },
    getCancelReasons: {
      method: 'GET',
      path: '/cancel-reasons',
      query: z
        .object({
          context: z.nativeEnum(CancelReasonContext).optional(),
        })
        .optional(),
      responses: {
        200: z.array(CancelReasonSchema),
      },
    },
    getSubscriptions: {
      method: 'GET',
      path: '/subscriptions',
      responses: {
        200: c.type<{ subscriptions: Subscription[] }>(),
      },
    },
    /** @deprecated */
    updateSubscriptionExtras: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extras',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.array(UpdateSubscriptionBodyExtraSchema),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },

    addSubscriptionExtra: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/add-extra',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraType: z.nativeEnum(ExtraType),
        startOrderId: z.string(),
        nthOrder: z.number(),
        quantity: z.number(),
        productVariantId: z.string(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    updateSubscriptionExtraQuantity: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extra-quantity',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        quantity: z.number(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    updateSubscriptionExtraFrequency: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extra-frequency',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        startOrderId: z.string(),
        nthOrder: z.number(),
        quantity: z.number(),
        extraType: z.nativeEnum(ExtraType),
        productVariantId: z.string(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    deleteSubscriptionExtra: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/delete-extra',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        cancelReasonId: z.string().optional(),
        cancelNotes: z.string().optional(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },

    updateSubscriptionRecipes: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/:feedingPlanId/update-recipes',
      pathParams: z.object({
        subscriptionId: z.string(),
        feedingPlanId: z.string(),
      }),
      body: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    updateSubscriptionServingSize: {
      method: 'POST',
      path: '/subscriptions/update-serving-size',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanId: z.string(),
        servingSize: z.nativeEnum(FeedingPlanServingSize),
      }),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    updateSubscriptionDeliveryDate: {
      method: 'POST',
      path: '/subscriptions/update-order-delivery-date',
      body: z.object({
        orderId: z.string(),
        deliveryDate: z.string(),
      }),
      responses: {
        200: c.type<{ orders: Order[] }>(),
      },
    },
    updateSubscriptionFeedingPlanType: {
      method: 'POST',
      path: '/subscriptions/update-subscription-feeding-plan-type',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanType: z.nativeEnum(FeedingPlanType),
      }),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    getGeneratedFeedingPlans: {
      method: 'POST',
      path: '/plans/generate',
      body: GeneratePlansBodySchema,
      responses: {
        200: c.type<{ feedingPlans: GeneratedPlan[] }>(),
      },
    },
    updateDogProfileWithNewRecommendation: {
      method: 'POST',
      path: '/dog/update-profile',
      body: z.object({
        dogId: z.string(),
        dogProfileInput: UpdateDogProfileInputSchema,
      }),
      responses: {
        200: c.type<{ data: UpdateDogProfileResult }>(),
      },
    },
    reviewUpdateSubscriptionServingSize: {
      method: 'POST',
      path: '/review-update-subscription-serving-size',
      body: z.object({
        subscriptionId: z.string(),
        servingSize: z.nativeEnum(FeedingPlanServingSize),
        feedingPlanId: z.string(),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof ReviewUpdateSubscriptionPayloadSchema> }>(),
      },
    },
    reviewUpdateSubscriptionFeedingPlanType: {
      method: 'POST',
      path: '/review-update-subscription-feeding-plan-type',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanType: z.nativeEnum(FeedingPlanType),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof ReviewUpdateSubscriptionPayloadSchema> }>(),
      },
    },
    reactivateSubscription: {
      method: 'POST',
      path: '/reactivate-subscription',
      body: z.object({
        deliveryDate: z.string(),
        preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof SubscriptionSchema> }>(),
      },
    },
  },
  {
    commonResponses: {
      400: ErrorResponseSchema,
      401: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
)
